import { inject, Injectable, InjectionToken } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, merge, Observable, of, shareReplay } from 'rxjs';
import { AiChatUiEventsService } from '../../../ai-chat-ui-events.service';

/**
 * Injection token for controlling file upload button visibility.
 * Consumers can provide this token to enable/disable the file upload feature.
 * Default value is `false` (feature disabled).
 */
export const AI_CHAT_FILE_UPLOAD_ENABLED = new InjectionToken<Observable<boolean>>('AI Chat File Upload Enabled', {
    providedIn: 'root',
    factory: () => of(false)
});

@Injectable({
    providedIn: 'root'
})
export class PromptBarService {
    public readonly showFileUpload$: Observable<boolean>;

    private readonly needToUploadFileSubject = new BehaviorSubject(false);
    private readonly uiEvents = inject(AiChatUiEventsService);
    private readonly fileUploadAlwaysEnabled$ = inject(AI_CHAT_FILE_UPLOAD_ENABLED);

    constructor() {
        const serverRequested$ = merge(this.needToUploadFileSubject.asObservable(), this.uiEvents.initThreadRequest.pipe(map(() => false)));

        this.showFileUpload$ = combineLatest([serverRequested$, this.fileUploadAlwaysEnabled$]).pipe(
            map(([serverRequested, featureEnabled]) => featureEnabled || serverRequested),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    public setNeedToUploadFile(needToUploadFile: boolean): void {
        this.needToUploadFileSubject.next(needToUploadFile);
    }
}
